import type { QueryClient } from '@tanstack/react-query'

import api from './api'
import type { DataReviewOption } from './dataReview'
import { invalidateLoanQueries } from './loanQueryKeys'
import { toCamelCase } from '@/utils/transform'

/**
 * Confirmation view — one editable view of a deal's populated and missing data,
 * and who confirmed what (work package H).
 *
 * The server owns what "pending" means: a figure is pending until a PERSON
 * confirms it, and auto-promotion is not a person. This module only types the
 * read model and carries a person's decisions back through ONE bulk route that
 * is all-or-nothing. It never decides a status, never counts one, and has no
 * verb that confirms without the caller naming the field.
 */

/** How a field stands, from the server. `derived` is the platform's own
 *  arithmetic — its inputs are what a person confirms. */
export type ConfirmationStatus =
  | 'pending'
  | 'confirmed'
  | 'derived'
  | 'conflicted'
  | 'missing_required'
  | 'missing_optional'
  | 'not_applicable'

export interface ConfirmationSource {
  /** stated · derived · suggested · confirmed · … */
  basis: string | null
  /** One line a reader can check: file · page · cell · date. */
  summary: string
  document: string | null
  sha256: string | null
  ingestionId: string | null
  page: string | null
  cellRef: string | null
  lineNo: string | null
  method: string | null
  attribution: string | null
  attributionWhy: string | null
  statedAt: string | null
  formula: string | null
  suggestionBasis: string | null
  /** The engine records no per-field confidence score; null unless it did. */
  confidence: number | null
}

export interface ConfirmationOf {
  by: string | null
  at: string | null
  rationale: string | null
  via: string | null
  kind: string | null
}

export interface ConfirmationQuestion {
  id: string
  kind: string
  /** Sources disagree (a conflict, a replacement, an unsupported value) rather
   *  than the engine flagging a value to look at. */
  conflict: boolean
  question: string | null
  value: unknown
  valueSource: string | null
  options: DataReviewOption[]
  actions: string[]
  ingestionId: string | null
}

export interface ConfirmationRevision {
  ingestionId: string
  /** The key the apply route names the proposal by. */
  field: string
  current: unknown
  incoming: unknown
  source: string | null
  from: string | null
  subject: string | null
  receivedAt: string | null
  why: string | null
  lenderDraft: boolean
}

export interface ConfirmationRow {
  field: string
  label: string
  group: string
  kind: 'text' | 'currency' | 'percent' | 'number' | 'date' | 'select'
  native: boolean
  unit: string | null
  options: string[]
  required: boolean
  help: string | null
  value: unknown
  status: ConfirmationStatus
  /** The lineage state: stated · derived · suggested · confirmed · … */
  state: string | null
  source: ConfirmationSource
  confirmation: ConfirmationOf | null
  confirmable: boolean
  confirmBlocked: string | null
  editable: boolean
  editBlocked: string | null
  clearable: boolean
  inConflict: boolean
  questions: ConfirmationQuestion[]
  revisions: ConfirmationRevision[]
  withdrawn: { at: string | null; by: string | null; from: unknown; reason: string | null } | null
  unreadModels: string[] | null
}

export interface ConfirmationSection {
  group: string
  rows: ConfirmationRow[]
}

export interface ConfirmationSummary {
  totalN: number
  confirmedN: number
  pendingN: number
  derivedN: number
  conflictedN: number
  /** Fields in conflict, whatever their status. */
  conflictN: number
  missingN: number
  missingRequiredN: number
  missingOptionalN: number
  notApplicableN: number
  requiredN: number
  confirmedByLast: string | null
  confirmedAtLast: string | null
}

export type ReviewState = 'not_required' | 'none' | 'awaiting_manager' | 'approved' | 'returned'

export interface ReviewTransition {
  id: string
  loanId: string
  state: Exclude<ReviewState, 'not_required' | 'none'>
  actor: string
  note: string | null
  fields: string[]
  summary: Partial<ConfirmationSummary> | null
  at: string | null
}

export interface ConfirmationReview {
  /** The administrator's switch for the manager step. */
  enabled: boolean
  state: ReviewState
  /** Whether THIS account may approve or return (`intake.approve`). */
  canApprove: boolean
  latest: ReviewTransition | null
  history: ReviewTransition[]
}

export interface ConfirmationView {
  loanId: string
  /** `property_name` on the wire; `toCamelCase` maps it to `property`. */
  property: string | null
  /** `asset_type` on the wire; `toCamelCase` maps it to `type`. */
  type: string | null
  readOnly: boolean
  summary: ConfirmationSummary
  review: ConfirmationReview
  sections: ConfirmationSection[]
}

export type ConfirmationVerb = 'confirm' | 'edit' | 'clear'

export interface ConfirmationAction {
  field: string
  action: ConfirmationVerb
  /** The new value, on the scale the deal editor sends. Required for `edit`. */
  value?: unknown
  /** The value the person was looking at; the server refuses the action if the
   *  deal has changed since, so nobody confirms what they did not see. */
  expectedValue?: unknown
  reason?: string
}

export interface ConfirmationApplied {
  field: string
  action: ConfirmationVerb
  outcome: 'confirmed' | 'edited' | 'cleared' | 'unchanged'
  from: unknown
  to: unknown
  eventId?: string
}

export interface ConfirmationResult {
  loanId: string
  applied: ConfirmationApplied[]
  changedN: number
  unchangedN: number
  derivation: { error?: string; derived?: Record<string, unknown> } | null
  review: ConfirmationReview
}

export interface QueueItem extends ReviewTransition {
  /** `property_name` on the wire; `toCamelCase` maps it to `property`. */
  property: string | null
  sponsor: string | null
  /** `asset_type` on the wire; `toCamelCase` maps it to `type`. */
  type: string | null
  stage: string | null
}

export interface ConfirmationQueue {
  enabled: boolean
  canApprove: boolean
  waiting: QueueItem[]
  recent: (ReviewTransition & { property: string | null })[]
}

export const confirmationKeys = {
  all: ['deal-confirmation'] as const,
  view: (loanId: string) => ['deal-confirmation', loanId] as const,
  queue: ['deal-confirmation-queue'] as const,
}

export const dealConfirmation = {
  view: (loanId: string) =>
    api
      .get(`/loans/${encodeURIComponent(loanId)}/confirmation`)
      .then((r) => toCamelCase<ConfirmationView>(r.data)),

  /** Confirm, edit or clear — all of the actions or none of them. */
  record: (loanId: string, fields: ConfirmationAction[], reason?: string) =>
    api
      .post(`/loans/${encodeURIComponent(loanId)}/confirmations`, {
        fields: fields.map((f) => ({
          field: f.field,
          action: f.action,
          // `value` is only sent for an edit: a confirm that carried a value
          // would read as a change.
          ...(f.action === 'edit' ? { value: f.value } : {}),
          ...('expectedValue' in f ? { expectedValue: f.expectedValue } : {}),
          ...(f.reason ? { reason: f.reason } : {}),
        })),
        ...(reason ? { reason } : {}),
      })
      .then((r) => toCamelCase<ConfirmationResult>(r.data)),

  approve: (loanId: string, note?: string) =>
    api
      .post(`/loans/${encodeURIComponent(loanId)}/confirmation/approve`, { note })
      .then((r) => toCamelCase<{ loanId: string; review: ReviewTransition }>(r.data)),

  /** Send the confirmed data back. The note is required. */
  returnToConfirmer: (loanId: string, note: string) =>
    api
      .post(`/loans/${encodeURIComponent(loanId)}/confirmation/return`, { note })
      .then((r) => toCamelCase<{ loanId: string; review: ReviewTransition }>(r.data)),

  queue: () =>
    api.get('/intake/confirmation-queue').then((r) => toCamelCase<ConfirmationQueue>(r.data)),
}

export interface ConfirmationRefusal {
  status: number | null
  code: string | null
  message: string
  /** Per-field refusals of a bulk write, when the server named them. */
  errors: { field: string; code: string; message: string }[]
}

/** What a failed call said, as something the screen can act on. An error body
 *  is not run through `toCamelCase` by the client, so it is read as sent. */
export function confirmationRefusal(error: unknown): ConfirmationRefusal {
  const response = (error as {
    response?: { status?: number; data?: { detail?: unknown } }
  } | null)?.response
  if (!response) {
    return { status: null, code: null, message: 'Could not reach the server. Please retry.', errors: [] }
  }
  const status = response.status ?? null
  const detail = response.data?.detail
  if (typeof detail === 'string') return { status, code: null, message: detail, errors: [] }
  if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
    const body = detail as Record<string, unknown>
    const errors = Array.isArray(body.errors)
      ? (body.errors as Record<string, unknown>[]).map((e) => ({
          field: String(e.field ?? ''),
          code: String(e.code ?? ''),
          message: String(e.message ?? ''),
        }))
      : []
    return {
      status,
      code: typeof body.code === 'string' ? body.code : null,
      message: typeof body.message === 'string' ? body.message : 'That was refused.',
      errors,
    }
  }
  if (Array.isArray(detail)) {
    const first = detail.find((d) => d && typeof d === 'object' && typeof (d as { msg?: unknown }).msg === 'string')
    return {
      status, code: null, errors: [],
      message: first ? String((first as { msg: string }).msg) : 'That request was not valid.',
    }
  }
  return { status, code: null, message: 'That could not be recorded. Please retry.', errors: [] }
}

/** Everything a confirmation can have changed, refreshed: the view, the queue,
 *  the deal and its lineage, and the pipeline's blank-cell markers. */
export async function invalidateAfterConfirmation(queryClient: QueryClient, loanId: string) {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: confirmationKeys.view(loanId) }),
    queryClient.invalidateQueries({ queryKey: confirmationKeys.queue }),
    queryClient.invalidateQueries({ queryKey: ['pipeline-review-gaps'] }),
    queryClient.invalidateQueries({ queryKey: ['data-review'] }),
    invalidateLoanQueries(queryClient, loanId),
  ])
}
